// hooks/productHook.js

import React, { useState, useEffect } from "react";
import {
  saveProduct,
  getProduct,
  getAllSubjects,
  getGrantablePermissions,
} from "./services/product-service";
import { ShowNotification } from "../../helpers/general/generalHelpers";

const initialProductState = {
  type: [],
  name: "",
  description: "",
  price: "",
  accessDurationDays: "",
  contentId: "",
  parentContentId: "",
  grantedRoleIds: [],
  isPurchased: false,
};

export function useProductHook(props) {
  const [loader, setLoader] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const [product, setProduct] = useState(initialProductState);
  const [toggleSwitch, settoggleSwitch] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [grantablePermissions, setGrantablePermissions] = useState([]);
  const [productType, setProductType] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const permissionOptions = React.useMemo(() =>
    grantablePermissions.map(p => ({
      value: p._id,
      label: `Access to: ${p.parent.charAt(0).toUpperCase() + p.parent.slice(1)}-${p.routePath}`
    })),
    [grantablePermissions]);

  const resetForm = () => {
    setProduct(initialProductState);
    setSelectedPermissions([]);
    setProductType([]);
    settoggleSwitch(true);
  };


  useEffect(() => {
    const productId = new URLSearchParams(props.location.search).get("id");
    setLoader(true);

    Promise.all([
      getAllSubjects(),
      getGrantablePermissions()
    ]).then(([subjectsRes, permissionsRes]) => {
      const allPermissions = permissionsRes.data.permissions || [];
      setSubjects(subjectsRes.data.subjects || []);
      setGrantablePermissions(allPermissions);

      if (productId) {
        setPageTitle("Edit Product");
        getProduct(productId).then(res => {
          const fetchedProduct = res.data.product;
          setProduct(fetchedProduct);
          setProductType(fetchedProduct.type || []);
          settoggleSwitch(fetchedProduct.isActive);
          if (fetchedProduct.grantedRoleIds && Array.isArray(fetchedProduct.grantedRoleIds)) {
            const preselected = allPermissions
              .filter(p => fetchedProduct.grantedRoleIds.includes(p._id))
              .map(p => ({
                value: p._id,
                label: `Access to: ${p.parent.charAt(0).toUpperCase() + p.parent.slice(1)}-${p.routePath}`
              }));
            setSelectedPermissions(preselected);
          }
        }).catch(err => console.error("Failed to fetch product", err))
          .finally(() => setLoader(false));
      } else {
        setPageTitle("Add Product");
        resetForm();
        setLoader(false);
      }
    }).catch(err => {
      console.error("Failed to fetch initial data for product form", err);
      setLoader(false);
    });

  }, [props.location.search]);

  // --- UPDATED to accept the form reference ---
  const saveProductHandler = async (e, values, formRef) => {
    setLoader(true);
    const productId = new URLSearchParams(props.location.search).get("id");

    const payload = {
      ...values,
      isActive: toggleSwitch,
    };

    try {
      // On edit, only send fields that are allowed to be changed if product is in use
      if (productId && product.isPurchased) {
        // --- CHANGE: Added price to the payload ---
        const restrictedPayload = {
          name: values.name,
          description: values.description,
          price: values.price, // <-- ADD THIS LINE
          isActive: toggleSwitch,
        };
        await saveProduct(restrictedPayload, productId); // Ensure productId is passed if service requires it
      } else {
        await saveProduct(payload, productId); // Ensure productId is passed if service requires it
      }

      const messageType = productId ? "updated" : "added";
      ShowNotification({
        type: "success",
        message: `Product has been ${messageType} successfully!`,
      });

      props.history.push({
        pathname: "/products-list",
        notification: { show: true, message: `Product has been ${messageType} successfully!` }
      });

    } catch (error) {
      const message = error?.response?.data?.message || "There was an error saving the product.";
      ShowNotification({ type: "error", message });
    } finally {
      setLoader(false);
    }
  };

  return {
    saveProductHandler,
    loader,
    pageTitle,
    product,
    toggleSwitch,
    settoggleSwitch,
    subjects,
    productType,
    setProductType,
    permissionOptions,
    selectedPermissions,
    setSelectedPermissions,
    setProduct, // Expose setProduct
    grantablePermissions, // Expose grantablePermissions
  };
}